import * as React from 'react';
import type { Accessors } from '#types/accessor.ts';
import {
  Circle as StyledCircle,
  CircleProps as StyledCircleProps,
} from '../primitives/Circle';
import { useChart } from '../providers/chart-data-provider';
import { useTooltip } from '../providers/tooltip-provider';

export type CircleProps<Datum, XResult, YResult> = StyledCircleProps & {
  datum: Datum;
  accessors: Accessors<Datum, XResult, YResult>;
};

// A utility function to retrieve a value for an axis and return the scaled version.
// @ts-ignore
const compose = (scale, accessor) => (d) => scale(accessor(d));

export function Circle<Datum, XResult, YResult>({
  accessors,
  datum,
  ...props
}: CircleProps<Datum, XResult, YResult>) {
  const { xScale, yScale } = useChart();
  const { showTooltip, hideTooltip } = useTooltip();

  const xPoint = compose(xScale, accessors.xAccessor);
  const yPoint = compose(yScale, accessors.yAccessor);

  const x = xPoint(datum);
  const y = yPoint(datum);

  const handleShowTooltip = () => {
    showTooltip({
      tooltipData: datum,
      tooltipLeft: x,
      tooltipTop: y,
    });
  };

  const handleHideTooltip = () => {
    hideTooltip();
  };

  return (
    <StyledCircle
      cx={x}
      cy={y}
      onFocus={handleShowTooltip}
      onMouseEnter={handleShowTooltip}
      onMouseLeave={handleHideTooltip}
      onBlur={handleHideTooltip}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}
